import { useState, useCallback } from 'react';
import { Settings, Moon, Sun, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthDialog from './AuthDialog';
import { useLoggedInAccounts } from '@/hooks/useLoggedInAccounts';
import { useTheme } from '@/hooks/useTheme';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { AccountSwitcher } from './AccountSwitcher';
import { Capacitor } from '@capacitor/core';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface LoginAreaProps {
  className?: string;
}

export function LoginArea({ className }: LoginAreaProps) {
  const { currentUser } = useLoggedInAccounts();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const closeAuthDialog = useCallback(() => setAuthDialogOpen(false), []);
  const { theme, setTheme } = useTheme();
  const { installable, installing, installed, install } = usePWAInstall();

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const handleInstall = async () => {
    await install();
  };
  
  // Don't show install option on native platforms (APK/iOS app)
  const isNative = Capacitor.isNativePlatform();
  // Show install option if app is installable OR not yet installed (link to instructions)
  const showInstallOption = !isNative && (installable || !installed);

  return (
    <div className={cn("inline-flex items-center justify-center gap-1", className)}>
      {currentUser ? (
        <AccountSwitcher onAddAccountClick={() => setAuthDialogOpen(true)} />
      ) : (
        <>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors">
                <Settings className="h-5 w-5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 p-2 animate-scale-in z-[100]">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  toggleTheme();
                }}
                className="flex items-center gap-2 cursor-pointer p-2 rounded-md"
              >
                {theme === 'dark' ? (
                  <Sun className="w-4 h-4 text-amber-400" />
                ) : (
                  <Moon className="w-4 h-4 text-violet-500" />
                )}
                <span>{theme === 'dark' ? 'light mode' : 'dark mode'}</span>
              </DropdownMenuItem>
              {showInstallOption && (
                <>
                  {installable ? (
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        handleInstall();
                      }}
                      className="flex items-center gap-2 cursor-pointer p-2 rounded-md"
                      disabled={installing}
                    >
                      <Download className="w-4 h-4 text-blue-500" />
                      <span>{installing ? 'installing...' : 'install app'}</span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem asChild>
                      <Link
                        to="/install"
                        className="flex items-center gap-2 cursor-pointer p-2 rounded-md"
                      >
                        <Download className="w-4 h-4 text-blue-500" />
                        <span>install app</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuItem asChild>
                <a
                  href="https://shakespeare.diy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 cursor-pointer p-2 rounded-md text-muted-foreground"
                >
                  <span>🎭</span>
                  <span>vibed with shakespeare</span>
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={() => setAuthDialogOpen(true)}
            className="px-5 py-2.5 rounded-full bg-white/50 dark:bg-black/30 backdrop-blur-sm text-foreground font-medium hover:bg-white/70 dark:hover:bg-black/50 transition-colors"
          >
            Join
          </button>
        </>
      )}

      <AuthDialog
        isOpen={authDialogOpen}
        onClose={closeAuthDialog}
      />
    </div>
  );
}
